import React, { useCallback, useRef, useState } from "react";
import * as THREE from "three";
import { IMeasureTool, ITool, Viewer } from "@treegram/viewer-core";

import { ModelingToolsContext, ModelingToolsContextProps } from "./modeling-tools-context.ts";
import MeasureColors from "../../../../models/enums/measure-colors.ts";

export enum ModelingType {
    Point = "POINT",
    Line = "LINE",
    Polyline = "POLYLINE",
    Surface = "SURFACE",
}

export enum ModelingMeasureRepresentation {
    LineVolume = "LINE_VOLUME",
    LineImprint = "LINE_IMPRINT",
    PolylinePanel = "POLYLINE_PANEL",
    ExtrusionRoom = "EXTRUSION_ROOM",
    Extrusion = "EXTRUSION",
    LineVolumeOffset = "LINE_VOLUME_OFFSET",
    LineVolumeDoor = "LINE_VOLUME_DOOR",
    FineSurface = "FINE_SURFACE",
    Disk = "DISK",
}

const ModelingToolsContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const viewerRef = useRef<Viewer | undefined>();

    const [isInDrawingMode, setIsInDrawingMode] = useState(false);
    const [isInEditingMode, setIsInEditingMode] = useState(false);

    const drawingType = useRef<ModelingType>(ModelingType.Point);
    const drawingMeasureRepresentation = useRef<ModelingMeasureRepresentation>(
        ModelingMeasureRepresentation.LineImprint,
    );
    const drawingColor = useRef<THREE.Color>(new THREE.Color(MeasureColors.Wall));
    const drawingAlpha = useRef<number>(1);
    const drawingDataInformations = useRef<Record<string, unknown> | undefined>(undefined);

    const editingType = useRef<ModelingType>(ModelingType.Point);
    const editingMeasureRepresentation = useRef<ModelingMeasureRepresentation>(
        ModelingMeasureRepresentation.LineImprint,
    );
    const editingColor = useRef<THREE.Color>(new THREE.Color(MeasureColors.Wall));
    const editingAlpha = useRef<number>(1);
    const editingDataInformations = useRef<Record<string, unknown> | undefined>(undefined);
    const editingPoints = useRef<THREE.Vector3[]>([]);

    const initialiseViewer = useCallback((viewer: Viewer) => {
        viewerRef.current = viewer;
    }, []);

    const setDrawingType = useCallback((type: ModelingType) => {
        drawingType.current = type;
    }, []);

    const setDrawingMeasureRepresentation = useCallback(
        (measureRepresentation: ModelingMeasureRepresentation) => {
            drawingMeasureRepresentation.current = measureRepresentation;
        },
        [],
    );

    const setDrawingColor = useCallback((color: THREE.Color) => {
        drawingColor.current = color;
    }, []);

    const setDrawingAlpha = useCallback((alpha: number) => {
        drawingAlpha.current = alpha;
    }, []);

    const setDrawingDataInformations = useCallback(
        (dataInformations: Record<string, unknown> | undefined) => {
            drawingDataInformations.current = dataInformations;
        },
        [],
    );

    const setEditingType = useCallback((type: ModelingType) => {
        editingType.current = type;
    }, []);

    const setEditingMeasureRepresentation = useCallback(
        (measureRepresentation: ModelingMeasureRepresentation) => {
            editingMeasureRepresentation.current = measureRepresentation;
        },
        [],
    );

    const setEditingColor = useCallback((color: THREE.Color) => {
        editingColor.current = color;
    }, []);

    const setEditingAlpha = useCallback((alpha: number) => {
        editingAlpha.current = alpha;
    }, []);

    const setEditingDataInformations = useCallback(
        (dataInformations: Record<string, unknown> | undefined) => {
            editingDataInformations.current = dataInformations;
        },
        [],
    );

    const setEditingPoints = useCallback((points: THREE.Vector3[]) => {
        editingPoints.current = points;
    }, []);

    // https://juhanajauhiainen.com/posts/how-to-make-typescript-understand-array-filter/
    const isDrawingTool = useCallback(
        (tool: ITool): tool is IMeasureTool<unknown> => "getIsDragging" in tool,
        [],
    );

    const isCurrentlyDrawing = useCallback(() => {
        const viewer = viewerRef.current;
        if (viewer == null) return false;

        const tools = viewer.getTools().values().filter(isDrawingTool);
        return tools.some((tool) => tool.getIsDragging());
    }, [isDrawingTool]);

    const contextProps: ModelingToolsContextProps = {
        initialiseViewer,

        isInDrawingMode,
        setIsInDrawingMode,

        isInEditingMode,
        setIsInEditingMode,

        isCurrentlyDrawing,

        drawingType: drawingType.current,
        setDrawingType,

        drawingMeasureRepresentation: drawingMeasureRepresentation.current,
        setDrawingMeasureRepresentation,

        drawingColor: drawingColor.current,
        setDrawingColor,

        drawingAlpha: drawingAlpha.current,
        setDrawingAlpha,

        drawingDataInformations: drawingDataInformations.current,
        setDrawingDataInformations,

        editingType: editingType.current,
        setEditingType,

        editingMeasureRepresentation: editingMeasureRepresentation.current,
        setEditingMeasureRepresentation,

        editingColor: editingColor.current,
        setEditingColor,

        editingAlpha: editingAlpha.current,
        setEditingAlpha,

        editingDataInformations: editingDataInformations.current,
        setEditingDataInformations,

        editingPoints: editingPoints.current,
        setEditingPoints,
    };

    return <ModelingToolsContext.Provider value={contextProps}>{children}</ModelingToolsContext.Provider>;
};

export default ModelingToolsContextProvider;
